<script setup lang="ts">
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'

import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify/framework'
import { formatDateOnly, formatDateTime, getFullName } from '@/utils/formatters'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useAuditForm } from '@/modules/audits/stores/useAuditRegisterStore/composables/useAuditForm'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'

import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import EmployeeProfileSidebar from '@/components/createdUi/EmployeeProfileSidebar.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import { downloadDocument } from '@/components/createdUi/downloadEntityDocument'
import SearchableSelectList from '@/components/createdUi/SearchableSelectList.vue'
import FormAudit from '@/modules/audits/stores/useAuditRegisterStore/components/FormAudit.vue'
import AuditCardAssignmentsTable from '@/modules/audits/stores/useAuditRegisterStore/components/AuditCardAssignmentsTable.vue'

import { ERouteNames } from '@/router/ERouteNames'
import type { IAuditDepartment } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditDepartment'
import type { IAuditCardAssignmentRow } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditCardAssignmentRow'
import type { IAuditAssignment } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditAssignment'
import type { ISearchableSelectItem } from '@/components/createdUi/SearchableSelectList.vue'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { AUDITS_ANCHORS, AUDITS_CONTEXTS } from '@/modules/onboarding/scenarios/audits/anchors'
import { AUDITS_CARD_DEMO_INDEX } from '@/modules/onboarding/scenarios/audits/adapter'
import { ONBOARDING_DEMO_QUERY } from '@/modules/onboarding/utils/demoRoute'

const props = defineProps<{
  auditId?: number
}>()

const {
  currentAudit,
  isEmployeeInfoModalOpen,
  selectedEmployeeId,
  showEmployeeInfoModal,
  onAuditFormSubmit,
} = useAuditForm()

const feedbackStore = useFeedbackStore()
const apiStore = useApiStore()
const auditRegisterStore = useAuditRegisterStore()
const { auditCardNonCompliancesCount } = storeToRefs(auditRegisterStore)
const userStore = useUserStore()
const permissionsStore = usePermissionsStore()
const { xs, smAndDown } = useDisplay()
const route = useRoute()
const router = useRouter()

const auditCardId = computed(() => Number(props.auditId) || Number(route.params.id) || 0)

const hasNonconformities = computed(() => Boolean(currentAudit.value.has_corrective_actions))

const activeDepartmentId = ref<string>('')

const isFormReady = ref(false)

const isDepartmentSelectDialogVisible = ref(false)
const isExportChecklistDialogVisible = ref(false)
const checklistAssignments = ref<IAuditAssignment[]>([])
const selectedChecklistEmployeeId = ref<string | null>(null)
const selectedChecklistFormat = ref<'docx' | 'xlsx'>('docx')
const isChecklistAssignmentsLoading = ref(false)
const isChecklistAuditorMenuOpen = ref(false)

const checklistAuditorSelectItems = computed<ISearchableSelectItem[]>(() =>
  checklistAssignments.value.map(assignment => ({
    id: assignment.employee.id,
    label: getFullName(assignment.employee),
  })),
)

const selectedChecklistAuditorLabel = computed(() => {
  if (!selectedChecklistEmployeeId.value) return ''

  const item = checklistAuditorSelectItems.value.find(
    i => String(i.id) === String(selectedChecklistEmployeeId.value),
  )

  return item?.label ?? ''
})

const onChecklistAuditorSelect = (item: ISearchableSelectItem) => {
  selectedChecklistEmployeeId.value = String(item.id)
  isChecklistAuditorMenuOpen.value = false
}

const isAuditCompleted = computed(() => currentAudit.value.status === 'completed')

const downloadingCardFormat = ref<'pdf' | 'docx' | null>(null)

async function downloadAuditCard(format: 'pdf' | 'docx') {
  const id = auditCardId.value
  if (!id || downloadingCardFormat.value)
    return

  downloadingCardFormat.value = format
  try {
    await downloadDocument(
      `/api/audit/audit/${id}/document/`,
      format,
      `audit_${id}`,
    )
  }
  finally {
    downloadingCardFormat.value = null
  }
}

const downloadAuditReport = async (format: 'docx' | 'xlsx') => {
  const auditId = auditCardId.value
  if (!auditId) return

  const depts = currentAudit.value.departments ?? []
  const departmentId = depts.length > 1
    ? (activeDepartmentId.value || String(depts[0]?.id || ''))
    : undefined

  if (depts.length > 1 && !departmentId) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Выберите отделение для экспорта отчёта',
    })
    return
  }

  await auditRegisterStore.exportAuditReport(
    auditId,
    format,
    departmentId,
  )
}

const openExportChecklistDialog = async () => {
  const auditId = auditCardId.value
  if (!auditId) return

  selectedChecklistEmployeeId.value = null
  selectedChecklistFormat.value = 'docx'
  isChecklistAuditorMenuOpen.value = false
  isExportChecklistDialogVisible.value = true
  isChecklistAssignmentsLoading.value = true

  try {
    checklistAssignments.value = await auditRegisterStore.fetchChecklistAssignments(auditId)
  } finally {
    isChecklistAssignmentsLoading.value = false
  }
}

const downloadChecklist = async () => {
  const auditId = auditCardId.value
  if (!auditId || !selectedChecklistEmployeeId.value) return

  isExportChecklistDialogVisible.value = false
  await auditRegisterStore.exportChecklist(
    auditId,
    selectedChecklistFormat.value,
    selectedChecklistEmployeeId.value,
  )
}

const resolveMyAssignmentId = async (auditId: number, departmentId: string): Promise<number | null> => {
  const userId = userStore.user?.id
  if (!userId) return null

  const ctx = auditRegisterStore.auditCardAssignmentContext
  const alreadyLoaded = ctx?.auditId === auditId
    && ctx.departmentId === departmentId
    && !auditRegisterStore.auditCardAssignmentsLoading

  if (!alreadyLoaded) {
    auditRegisterStore.setAuditCardAssignmentContext(auditId, departmentId)
    await auditRegisterStore.fetchAuditCardAssignments()
  }

  const row = auditRegisterStore.auditCardAssignments.find(
    a => a.auditor.id === userId,
  )

  return row?.assignment_id ?? null
}

const selectDepartmentAndStart = async (dept: IAuditDepartment): Promise<void> => {
  const id = auditCardId.value
  if (!id) return

  const departmentId = String(dept.id)
  const assignmentId = await resolveMyAssignmentId(id, departmentId)

  if (!assignmentId) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Не найдено назначение для текущей проверки',
    })
    return
  }

  auditRegisterStore.currentAuditedDepartment = {
    id: departmentId,
    name: dept.name,
    short_name: dept.short_name || '',
  }

  await auditRegisterStore.processAudit(id, departmentId, assignmentId)

  isDepartmentSelectDialogVisible.value = false

  await router.push({
    name: ERouteNames.AUDIT_PROCESS,
    params: {
      id,
      departmentId: dept.id,
    },
    query: { assignment: assignmentId },
  })
}

const isManager = computed(() => {
  const managerId = currentAudit.value.manager?.id
  return userStore.user?.id === managerId
})

watch(
  () => currentAudit.value.departments,
  depts => {
    if (!depts?.length) return
    const ids = depts.map(d => String(d.id))
    if (!activeDepartmentId.value || !ids.includes(activeDepartmentId.value)) {
      activeDepartmentId.value = ids[0]
    }
  },
  { immediate: true },
)

const onAssignmentRowClick = async (row: IAuditCardAssignmentRow) => {
  if (!isManager.value) return

  const id = Number(route.params.id)
  const departmentId = activeDepartmentId.value

  if (!id || !departmentId) return

  await auditRegisterStore.processAudit(
    id,
    departmentId,
    row.assignment_id,
  )

  await router.push({
    name: ERouteNames.AUDIT_PROCESS,
    params: { id, departmentId },
    query: { assignment: row.assignment_id },
  })
}

/**
 * Проверка: является ли текущий юзер одним из назначенных аудиторов
 */
const isAuditor = computed(() => {
  const userId = userStore.user?.id
  if (!userId) return false

  return auditRegisterStore.auditCardAssignments.some(
    row => row.auditor.id === userId,
  )
})

/**
 * Кнопка видна только если текущий пользователь является одним из назначенных аудиторов
 */
const isCanStartAudit = computed(() => isAuditor.value)

const startAudit = async (): Promise<void> => {
  const id = Number(route.params.id)
  if (!id) return

  const depts = currentAudit.value.departments ?? []

  if (!depts.length) return

  // Если отделений больше 1 — показываем диалог
  if (depts.length > 1) {
    isDepartmentSelectDialogVisible.value = true
    return
  }

  // Если ровно одно — запускаем сразу
  await selectDepartmentAndStart(depts[0])
}

const showEditDialog = ref(false)

const editAudit = async () => {
  const id = auditCardId.value
  if (id) {
    await auditRegisterStore.fetchAuditAssignmentsRaw(id)
  }
  showEditDialog.value = true
}

const goToNonconformities = () => {
  const id = auditCardId.value
  if (!id) return

  router.push({
    name: ERouteNames.AUDIT_NONCONFORMITIES,
    params: { id: String(id) },
  })
}

const onEditClose = async () => {
  showEditDialog.value = false
  const id = auditCardId.value
  if (id) {
    await auditRegisterStore.fetchAuditById(id)
  }
}

const displayStartTime = computed(() => formatDateOnly(currentAudit.value.planned_start))
const displayEndTime = computed(() => formatDateOnly(currentAudit.value.planned_end))
const displayActualStart = computed(() => formatDateOnly(currentAudit.value.actual_start))
const displayActualEnd = computed(() => formatDateOnly(currentAudit.value.actual_end))
const displayManagerName = computed(() => getFullName(currentAudit.value.manager ?? {}))

const getStatusText = (status: string | undefined) => {
  if (!status) return 'Ожидает'
  return apiStore.responsibilityEntriesStatuses[status as keyof typeof apiStore.responsibilityEntriesStatuses] || status
}

/*
 * Демо-режим онбординга: карточка открыта на учебном аудите (id из демо-данных онбординга)
 * или онбординг запущен из карточки реального аудита (demoOverrideId).
 * Id учебного аудита фиктивный: карточка берётся из демо-данных, назначения — из учебной ручки бека по id учебного аудита.
 */
const onboardingStore = useOnboardingStore()
const demoOverrideId = ref<number | null>(null)

// Вкладка отделения реального аудита до показа учебного — после возврата открываем её же
let realDepartmentId = ''
const demoItem = computed(() => onboardingStore.findDemoItem('audit', demoOverrideId.value ?? auditCardId.value))
const isDemo = computed(() => Boolean(demoItem.value))
// Id для запроса назначений: при запуске обучения из реального аудита в маршруте остаётся его id
const cardEntityId = computed(() => Number(demoItem.value?.detail.id ?? auditCardId.value))

function applyDemoAudit() {
  if (!demoItem.value)
    return

  const detail = JSON.parse(JSON.stringify(demoItem.value.detail))

  auditRegisterStore.currentAudit = { ...detail, departments: detail.departments ?? [], audit_assignments: detail.audit_assignments ?? [] }
  auditRegisterStore.editableAudit = JSON.parse(JSON.stringify(auditRegisterStore.currentAudit))
  isFormReady.value = true
}

// Карточка, встроенная в форму несоответствия (auditId в пропсах), в онбординге аудита не участвует
useOnboardingContext({
  names: props.auditId ? [] : [AUDITS_CONTEXTS.card],
  scenarioId: 'audits.card',
  root: () => document.body,
  actions: {
    showDemo: () => {
      realDepartmentId = activeDepartmentId.value
      demoOverrideId.value = onboardingStore.demo.audit?.[AUDITS_CARD_DEMO_INDEX]?.detail.id ?? null
      applyDemoAudit()
    },
    showReal: async () => {
      demoOverrideId.value = null
      if (auditCardId.value && !isDemo.value) {
        /*
         * Вкладку возвращаем до загрузки: иначе watch по отделениям сначала откроет первую вкладку
         * (порядок отделений в ответе бека не постоянный), и два запроса назначений перепишут счётчики друг другу
         */
        activeDepartmentId.value = realDepartmentId
        await loadAuditData(auditCardId.value)
      }
    },
    // Как клик руководителя по строке: проверка первого назначения во вкладке отделения, но на учебном аудите
    openDemoProcess: async () => {
      const assignmentId = auditRegisterStore.auditCardAssignments[0]?.assignment_id
      if (!isDemo.value || !activeDepartmentId.value || !assignmentId)
        return

      await auditRegisterStore.processAudit(cardEntityId.value, activeDepartmentId.value, assignmentId)
      await router.push({
        name: ERouteNames.AUDIT_PROCESS,
        params: { id: cardEntityId.value, departmentId: activeDepartmentId.value },
        query: { assignment: assignmentId, [ONBOARDING_DEMO_QUERY]: '1' },
      })
    },
  },
})

const loadAuditData = async (id: number) => {
  if (!id) return
  if (isDemo.value) {
    applyDemoAudit()
    return
  }
  isFormReady.value = false
  try {
    await auditRegisterStore.fetchAuditById(id)
  } finally {
    isFormReady.value = true
  }
}

onMounted(() => {
  if (auditCardId.value) {
    loadAuditData(auditCardId.value)
  }
})

watch(auditCardId, newId => {
  if (newId) {
    loadAuditData(newId)
  }
})
</script>

<template>
  <div v-if="isFormReady" class="d-flex flex-column gap-4 w-100" style="max-width: 1120px;">
    <div
      v-if="!props.auditId"
      class="d-flex gap-2"
      :class="smAndDown ? 'w-100 flex-column' : 'align-center justify-space-between'"
    >
      <div
        v-if="isCanStartAudit && !isAuditCompleted"
        class="d-flex align-center gap-2"
        :class="{ 'w-100': smAndDown }"
      >
        <VBtn
          :class="{ 'w-100': smAndDown }"
          variant="elevated"
          :loading="feedbackStore.isGlobalLoading"
          class="general-button"
          :data-onboarding="AUDITS_ANCHORS.card.start"
          @click="startAudit"
        >
          Начать проверку
        </VBtn>
      </div>
      <div
        class="d-flex gap-2"
        :class="[
          smAndDown ? 'w-100 flex-column' : 'align-center',
          { 'ms-auto': !smAndDown && !(isCanStartAudit && !isAuditCompleted) },
        ]"
      >
        <ReportTemplateMenu object-type="audit" :object-id="auditCardId" variant="toolbar" />
        <OnboardingLaunchButton />
        <VMenu location="bottom end">
          <template #activator="{ props: cardMenuProps }">
            <VBtn
              v-bind="cardMenuProps"
              :data-onboarding="AUDITS_ANCHORS.card.downloads"
              :class="{ 'w-100': smAndDown }"
              variant="outlined"
              color="secondary"
              prepend-icon="ri-download-line"
              :loading="Boolean(downloadingCardFormat)"
            >
              Скачать карточку
            </VBtn>
          </template>
          <VList
            density="compact"
            min-width="180"
          >
            <VListItem
              prepend-icon="ri-file-pdf-2-line"
              title="PDF-документ"
              :disabled="Boolean(downloadingCardFormat)"
              @click="downloadAuditCard('pdf')"
            />
            <VListItem
              prepend-icon="ri-file-word-2-line"
              title="Документ Word"
              :disabled="Boolean(downloadingCardFormat)"
              @click="downloadAuditCard('docx')"
            />
          </VList>
        </VMenu>
        <VMenu
          v-if="isAuditCompleted"
          location="bottom end"
        >
          <template #activator="{ props: reportMenuProps }">
            <VBtn
              v-bind="reportMenuProps"
              :class="{ 'w-100': smAndDown }"
              variant="outlined"
              color="secondary"
              prepend-icon="ri-download-line"
              :loading="feedbackStore.isGlobalLoading"
            >
              Скачать отчёт
            </VBtn>
          </template>
          <VList
            density="compact"
            min-width="180"
          >
            <VListItem
              prepend-icon="ri-file-word-2-line"
              title="Документ Word"
              :disabled="feedbackStore.isGlobalLoading"
              @click="downloadAuditReport('docx')"
            />
            <VListItem
              prepend-icon="ri-file-excel-2-line"
              title="Документ Excel"
              :disabled="feedbackStore.isGlobalLoading"
              @click="downloadAuditReport('xlsx')"
            />
          </VList>
        </VMenu>
        <VBtn
          v-if="hasNonconformities"
          :class="{ 'w-100': smAndDown }"
          variant="outlined"
          color="secondary"
          prepend-icon="ri-bill-line"
          :data-onboarding="AUDITS_ANCHORS.card.nonconformities"
          @click="goToNonconformities"
        >
          Несоответствия
        </VBtn>
        <VBtn
          :class="{ 'w-100': smAndDown }"
          variant="outlined"
          color="secondary"
          prepend-icon="ri-download-line"
          :loading="feedbackStore.isGlobalLoading || isChecklistAssignmentsLoading"
          :data-onboarding="AUDITS_ANCHORS.card.checklist"
          @click="openExportChecklistDialog"
        >
          Скачать чек-лист
        </VBtn>
        <VBtn
          v-if="permissionsStore.modules.audit?.edit && currentAudit.editable_fields?.length"
          :class="{ 'w-100': smAndDown }"
          variant="outlined"
          color="secondary"
          prepend-icon="ri-edit-2-line"
          :data-onboarding="AUDITS_ANCHORS.card.edit"
          @click="editAudit"
        >
          Редактировать
        </VBtn>
      </div>
    </div>

    <VCard
      :elevation="props.auditId ? 0 : undefined"
      :flat="Boolean(props.auditId)"
      class="d-flex flex-column gap-4 pa-4 w-100"
      :class="{ 'shadow-none': props.auditId }"
      :max-width="1120"
    >
      <div class="d-flex align-center justify-space-between gap-2">
        <h3 v-if="currentAudit.name" class="text-h5">
          {{ currentAudit.name }}
        </h3>

        <VChip
          v-if="auditCardId"
          :class="currentAudit.status"
          class="status-badge"
          size="small"
        >
          {{ getStatusText(currentAudit.status) }}
        </VChip>
      </div>

      <div class="d-flex gap-2" :class="smAndDown ? 'w-100 flex-column' : 'align-center justify-between'">
        <div class="w-100">
          <VTextField
            :model-value="displayStartTime"
            variant="outlined"
            readonly
            hide-details
            disabled
            class="invisible-readonly"
          >
            <template #label>
              <span>План. дата начала</span>
            </template>
            <template #append-inner>
              <VIcon icon="ri-calendar-line" size="20" />
            </template>
          </VTextField>
        </div>

        <div class="w-100">
          <VTextField
            :model-value="displayEndTime"
            variant="outlined"
            readonly
            hide-details
            disabled
            class="invisible-readonly"
          >
            <template #label>
              <span>План. дата окончания</span>
            </template>
            <template #append-inner>
              <VIcon icon="ri-calendar-line" size="20" />
            </template>
          </VTextField>
        </div>
      </div>

      <div
        v-if="isAuditCompleted"
        class="d-flex gap-2"
        :class="smAndDown ? 'w-100 flex-column' : 'align-center justify-between'"
      >
        <div class="w-100">
          <VTextField
            :model-value="displayActualStart"
            variant="outlined"
            readonly
            hide-details
            disabled
            class="invisible-readonly"
          >
            <template #label>
              <span>Факт. дата начала</span>
            </template>
            <template #append-inner>
              <VIcon icon="ri-calendar-line" size="20" />
            </template>
          </VTextField>
        </div>

        <div class="w-100">
          <VTextField
            :model-value="displayActualEnd"
            variant="outlined"
            readonly
            hide-details
            disabled
            class="invisible-readonly"
          >
            <template #label>
              <span>Факт. дата окончания</span>
            </template>
            <template #append-inner>
              <VIcon icon="ri-calendar-line" size="20" />
            </template>
          </VTextField>
        </div>
      </div>

      <VTextField
        :model-value="displayManagerName"
        variant="outlined"
        readonly
        hide-details
        disabled
        class="invisible-readonly"
        label="Руководитель аудита"
      />

      <div v-if="currentAudit.departments?.length && activeDepartmentId" class="d-flex flex-column ga-4">
        <VTabs
          v-model="activeDepartmentId"
          density="compact"
          color="primary"
          show-arrows
          :data-onboarding="AUDITS_ANCHORS.card.departments"
        >
          <VTab
            v-for="dept in currentAudit.departments"
            :key="dept.id"
            :value="String(dept.id)"
          >
            {{ dept.short_name || dept.name }}
          </VTab>
        </VTabs>

        <AuditCardAssignmentsTable
          v-if="auditCardId && activeDepartmentId"
          :data-onboarding="AUDITS_ANCHORS.card.assignments"
          :key="activeDepartmentId"
          :audit-id="cardEntityId"
          :department-id="activeDepartmentId"
          :is-manager="isManager"
          @row-click="onAssignmentRowClick"
          @auditor-click="showEmployeeInfoModal"
        />
      </div>

      <CustomDivider />

      <footer class="d-flex flex-column gap-2 body-2">
        <div class="d-flex gap-2 align-center">
          <VIcon icon="ri-user-3-line" size="16" />
          <span class="cursor-pointer">
            Создатель: {{ getFullName(currentAudit.created_by ?? {}) }}
          </span>
        </div>

        <div class="d-flex gap-2 align-center">
          <VIcon icon="ri-time-line" size="16" />
          <span>
            Дата создания: {{ formatDateTime(currentAudit.created_at) }}
          </span>
        </div>

        <div
          v-if="currentAudit.is_overdue"
          class="d-flex gap-2 align-center required-label"
        >
          <VIcon icon="ri-alert-line" size="16" />
          <span>Сроки проведения нарушены</span>
        </div>
      </footer>
    </VCard>
  </div>

  <div v-else class="d-flex align-mid justify-center" />

  <EmployeeProfileSidebar
    v-model="isEmployeeInfoModalOpen"
    :employee-id="selectedEmployeeId"
  />

  <VDialog v-model="isDepartmentSelectDialogVisible" max-width="500" :fullscreen="false">
    <VCard>
      <VCardTitle class="d-flex justify-space-between align-center pa-4">
        <span>Выберите отделение для проверки</span>
        <CustomCloseButton :close-action="() => isDepartmentSelectDialogVisible = false" />
      </VCardTitle>

      <VCardText class="pa-4 d-flex flex-column gap-3">
        <VBtn
          v-for="dept in currentAudit.departments"
          :key="dept.id"
          variant="outlined"
          color="primary"
          class="department-select-btn justify-start w-100"
          @click="selectDepartmentAndStart(dept)"
        >
          {{ dept.short_name || dept.name }}
        </VBtn>
      </VCardText>
    </VCard>
  </VDialog>

  <VDialog v-model="isExportChecklistDialogVisible" max-width="500" :fullscreen="false">
    <VCard>
      <VCardTitle class="d-flex justify-space-between align-center pa-4">
        <span>Скачивание чек-листов</span>
        <CustomCloseButton :close-action="() => isExportChecklistDialogVisible = false" />
      </VCardTitle>

      <VCardText class="pa-4 d-flex flex-column ga-4">
        <div v-if="isChecklistAssignmentsLoading" class="d-flex justify-center py-4">
          <AnimatedLogoLoader />
        </div>

        <template v-else>
          <VMenu
            v-if="checklistAssignments.length"
            v-model="isChecklistAuditorMenuOpen"
            :close-on-content-click="false"
            location="bottom"
            content-class="entity-form-menu"
          >
            <template #activator="{ props: menuProps }">
              <VTextField
                v-bind="menuProps"
                :model-value="selectedChecklistAuditorLabel"
                label="Аудитор"
                variant="outlined"
                readonly
                hide-details
                persistent-placeholder
                placeholder="Аудитор"
                class="invisible-readonly"
                :append-inner-icon="isChecklistAuditorMenuOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
              />
            </template>

            <SearchableSelectList
              :items="checklistAuditorSelectItems"
              placeholder="Поиск..."
              @select="onChecklistAuditorSelect"
            />
          </VMenu>

          <div v-else class="text-medium-emphasis text-center">
            Нет аудиторов для экспорта
          </div>

          <VRadioGroup
            v-model="selectedChecklistFormat"
            inline
            hide-details
            density="compact"
            color="primary"
            class="flex-grow-0"
          >
            <VRadio
              label="Формат Word"
              value="docx"
            />
            <VRadio
              label="Формат Excel"
              value="xlsx"
              class="ms-5"
            />
          </VRadioGroup>

          <div class="d-flex justify-end">
            <VBtn
              variant="elevated"
              color="primary"
              class="general-button"
              :disabled="!selectedChecklistEmployeeId"
              :loading="feedbackStore.isGlobalLoading"
              @click="downloadChecklist"
            >
              Скачать
            </VBtn>
          </div>
        </template>
      </VCardText>
    </VCard>
  </VDialog>

  <FormAudit v-if="showEditDialog" :embedded="true" @close="onEditClose" />
</template>

<style scoped lang="scss">
/* VBtn режет длинный текст nowrap'ом; height: auto — только чтобы вторая строка не обрезалась */
.department-select-btn {
  height: auto !important;
  min-height: var(--v-btn-height);

  :deep(.v-btn__content) {
    white-space: normal;
    text-align: start;
    overflow-wrap: break-word;
  }
}
</style>
